// ── What a downloaded file is CALLED (browser side) ──────────────────────────
//
// Mirror of the naming rules in api/utils/downloadName.js — keep the two in
// step, like the stone-code parsers. The server is the authority (it can also
// ask the database for a stored mimetype); this copy exists so the name we SEND
// is already right, which keeps downloads correct even against a server that
// has not been updated yet, and so a same-origin `download` attribute and the
// transfer center show the real name.
//
// Why it exists: a file's display name and its real name are different things
// here. The File Manager stores `name` WITHOUT its extension, Digital Marketing
// lets the person type a title for each file, and the viewer used to hand that
// title straight to the download — so "Front view" landed on the person's disk
// with no .jpg and the operating system could not tell what it was.

const SAME_EXT = [
  ['.jpg', '.jpeg', '.jpe', '.jfif'],
  ['.tif', '.tiff'],
  ['.htm', '.html'],
  ['.mpg', '.mpeg'],
  ['.mid', '.midi'],
  ['.yml', '.yaml'],
];
const canonExt = (e) => {
  for (const group of SAME_EXT) if (group.includes(e)) return group[0];
  return e;
};

// The trailing ".ext" of a name, lowercased, or ''. Needs at least one LETTER:
// "Block 12.5" ends in ".5", a number in a title, not a format. .tar.gz counts
// as one extension.
const COMPOUND_EXT = /\.tar\.(?:gz|bz2|xz|zst)$/i;
const SIMPLE_EXT = /\.([A-Za-z0-9]*[A-Za-z][A-Za-z0-9]{0,9})$/;
export function extensionOf(s) {
  const text = String(s == null ? '' : s);
  const compound = COMPOUND_EXT.exec(text);
  if (compound) return compound[0].toLowerCase();
  const simple = SIMPLE_EXT.exec(text);
  return simple ? `.${simple[1].toLowerCase()}` : '';
}

// A name safe to hand to the browser: no directory part, no control characters,
// no trailing dots or spaces, single spaces. Whitespace means the ASCII space
// ONLY (never \s or trim()): a name stored as latin1 holds real bytes in the
// 0x80-0xFF range and U+00A0 is one of them, so it must survive untouched.
export function cleanFileName(s) {
  let out = String(s == null ? '' : s).replace(/\\/g, '/');
  out = out.slice(out.lastIndexOf('/') + 1);
  // eslint-disable-next-line no-control-regex
  return out.replace(/[\u0000-\u001f\u007f]/g, '').replace(/ {2,}/g, ' ').replace(/^ +| +$/g, '').replace(/[. ]+$/, '');
}

// The last path segment of a URL or disk name, without query or hash.
export function fileNameFromUrl(url) {
  const raw = String(url == null ? '' : url).split(/[?#]/)[0];
  const last = raw.slice(raw.lastIndexOf('/') + 1);
  try { return decodeURIComponent(last); } catch (_) { return last; }
}

const MAX_NAME = 200;

// `name` is what the caller wants the file called (a title, possibly with no
// extension, possibly empty); `source` is anything that carries the file's REAL
// extension — a disk name, an /uploads/ URL, an original file name. The result
// always ends with that real extension.
export function withExtension(name, source) {
  const sourceName = fileNameFromUrl(source);
  let base = cleanFileName(name) || cleanFileName(sourceName) || 'file';
  if (base.length > MAX_NAME) base = base.slice(0, MAX_NAME).trim();
  const want = extensionOf(sourceName);
  if (!want) return base;
  if (canonExt(extensionOf(base)) === canonExt(want)) return base;
  return base + want;
}

// The file name out of a Content-Disposition header, or ''. Reads the RFC 5987
// `filename*` form first (what a Unicode name arrives as), then the quoted and
// bare `filename`. The quoted form is NOT percent-decoded: it is literal text,
// and a name like "100% done.jpg" used to throw in decodeURIComponent.
export function parseContentDisposition(header) {
  if (!header) return '';
  const text = String(header);
  const star = /filename\*\s*=\s*[^']*'[^']*'([^;]+)/i.exec(text);
  if (star) {
    try { return decodeURIComponent(star[1].trim()); } catch (_) { /* fall through to filename= */ }
  }
  const quoted = /filename\s*=\s*"((?:[^"\\]|\\.)*)"/i.exec(text);
  if (quoted) return quoted[1].replace(/\\(.)/g, '$1');
  const bare = /filename\s*=\s*([^;]+)/i.exec(text);
  return bare ? bare[1].trim() : '';
}
